import React from 'react';
import {
  ComponentRendering,
  ImageField,
  RichTextField,
  TextField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'src/jss/lib/component-props';
import { FilterValuePropertyFromFields } from 'src/utils/mapWithoutValueProperty';
import {
  Heading,
  ScrollLinkWrapper,
  TextWithSideImage as TextWithSideImageComponent,
} from '@ads-core/components';
import { SitecoreImage, SitecoreRichText, SitecoreText } from '@alliander-fe/jss-utils';
import { useIntersection } from 'src/hooks/useIntersection';
import { AdditionalFields } from 'src/components/TableOfContentsContainer/types/TableOfContentsComponentRendering';
import { useDynamicThreshold } from 'src/hooks/useDynamicThreshold';

export type TextWithSideImageProps = ComponentProps & {
  fields: {
    image: ImageField;
    imagePosition: {
      value: 'left' | 'right';
    };
    text: RichTextField;
    title: TextField;
  };
  params?: {
    anchor?: string;
  };
  rendering: ComponentRendering & AdditionalFields;
};

export type FilteredTextWithSideImageProps = FilterValuePropertyFromFields<
  TextWithSideImageProps['fields']
>;

export const TextWithSideImage = (props: TextWithSideImageProps) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const additionalFields = props.rendering.additionalFields;
  const threshold = useDynamicThreshold({ ref });

  // The intersection observer is used to set the intersecting state.
  // This way we can set the table of content item as active.
  useIntersection({
    callBack: (isIntersecting: boolean) => {
      if (isIntersecting && additionalFields?.anchor) {
        additionalFields?.isIntersecting(additionalFields?.anchor ?? '');
      }
    },
    ref,
    threshold: threshold,
  });

  if (props.fields) {
    return (
      <div ref={ref}>
        <ScrollLinkWrapper anchorId={additionalFields?.anchor ?? props.params?.anchor ?? undefined}>
          <TextWithSideImageComponent
            title={
              <Heading size="paragraph" fontWeight="semiBold" isResponsive={false} as="h2">
                <SitecoreText field={props.fields.title} editable />
              </Heading>
            }
            text={<SitecoreRichText field={props.fields.text} editable />}
            image={<SitecoreImage field={props.fields.image} editable />}
            alignment={props.fields.imagePosition.value}
            isContained={Boolean(additionalFields?.isContained)}
          />
        </ScrollLinkWrapper>
      </div>
    );
  }

  return null;
};

export default withDatasourceCheck()<TextWithSideImageProps>(TextWithSideImage);
